import { useCallback, useEffect, useState } from "react";
import { Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";

import { employmentHistoryGet } from "../api";
import { dateFormatter, dateGetter } from "../common/utils";
import { getLocaleText } from "../actions/datagrid";
import { contextMenu } from "../common/grid";

export function EmploymentHistory() {
  const { t } = useTranslation();

  const [personsHistory, setPersonsHistory] = useState(null); // json

  async function fetchData() {
    const personHistory = await employmentHistoryGet();
    const persons = [];
    for (const person of personHistory) {
      person.employmentPeriods.forEach((period, idx) => {
        persons.push({
          id: `${person.personId}-${idx}`,
          name: person.personName,
          employmentStart: period.employmentStartDate,
          contractStart: period.contractStartDate,
          contractEnd: period.contractEndDate,
          contractType: period.contractType,
          fullTime: period.fullTimeEquivalent,
          endCode: period.endCode,
        });
      });
    }
    setPersonsHistory(persons);
  }

  useEffect(() => {
    fetchData();
  }, []);

  const endCodeValueGetter = (params) => {
    const endCode = params.data?.endCode;
    if (endCode && endCode !== 255) return t(`EndCode${endCode}`);
  };

  const columnDefs = [
    {
      field: "name",
      rowGroup: true,
      hide: true,
    },
    {
      field: "employmentStart",
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
      rowGroup: true,
      hide: true,
    },
    {
      field: "contractStart",
      headerName: t("ContractStartDate", "Työsopimuksen alkupvm."),
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
    },
    {
      field: "contractEnd",
      headerName: t("EndofContract", "Päättymisajankohta"),
      filter: "agDateColumnFilter",
      valueGetter: dateGetter,
      valueFormatter: dateFormatter,
    },
    {
      field: "contractType",
      headerName: t("Type", "Tyyppi"),
      valueGetter: (params) =>
        params.data &&
        (params.data[params.colDef.field] === "fixedContract"
          ? t("FixedDuration", "Määräaikainen")
          : t("UntilFurtherNotice", "Toistaiseksi voimassaoleva")),
    },
    {
      field: "fullTime",
      headerName: t("PartTimePercentage", "Osa-aikaprosentti"),
    },
    {
      field: "endCode",
      headerName: t("EndCode", "Työsuhteen päättämisen syy"),
      valueGetter: endCodeValueGetter,
    },
  ];

  const getContextMenuItems = useCallback((params) => contextMenu(t, params), [t]);

  return (
    <>
      <h2 className="viewheading">{t("PersonnelAndEmploymentInfo", "Henkilöstö ja työsuhdetiedot")}</h2>

      <Row className="ag-theme-material styles-ag mt-3 flex-grow-1">
        <AgGridReact
          getContextMenuItems={getContextMenuItems}
          columnDefs={columnDefs}
          rowData={personsHistory}
          suppressAggFuncInHeader
          //groupDefaultExpanded
          autoGroupColumnDef={{
            headerName: `${t("Name", "Nimi")} > ${t("StartDate", "Työsuhteen aloituspäivä")}`,
            cellRendererParams: {
              suppressCount: true, // turn off the row count (in order to skip default stack counting)
              innerRenderer: (params) => {
                // our inner renderer in charge of child nodes counting
                if (params.node.group) {
                  let label = params.value ? params.value : "-";
                  if (label instanceof Date) {
                    label = dateFormatter(params);
                  }
                  return label + " (" + params.node.childrenAfterFilter.length + ")";
                }
              },
            },
          }}
          animateRows
          suppressCellFocus
          enableCellTextSelection
          onFirstDataRendered={(params) => params.api.sizeColumnsToFit()}
          getLocaleText={(params) => getLocaleText(t, params)}
          debug={import.meta.env.DEV}
        />
      </Row>
    </>
  );
}
